import React from "react"
import { Link as RouterLink, Redirect, useLocation, useParams } from "react-router-dom"
import { useIntl } from "react-intl"
import { Box, Divider, ForwardIcon, Link, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageBreadcrumbHeader } from "#/features/global-ui/page-header"
import { Title } from "#/services/page-title/Title"
import { Paths } from "#/utils/Paths"
import { useHelpCenterCategoryBySlug } from "#/services/help-center/useGetHelpCenter"
import { FullScreenLoading } from "#/components/read/shared/FullScreenLoading"
import { ErrorPage } from "#/components/views/app/ErrorPage"
import { helpCenterArticlePath, helpCenterCategoryPath } from "#/services/help-center/urls"
import type { HelpCenterArticleNavState, HelpCenterCategory, HelpCenterSubCategory } from "#/services/help-center/types"
import { FaqsBreadcrumbs } from "#/pages/faqs/components/FaqsBreadcrumbs"
import { usePageViewTracking } from "#/services/analytics/usePageViewTracking"
import { faqsLabelMessage } from "#/pages/faqs/messages"

export function FaqsCategoryPage(): JSX.Element {
  return <FaqsCategoryPageContent />
}

function FaqsCategoryPageContent(): JSX.Element {
  const intl = useIntl()
  const { categorySlug } = useParams<{ categorySlug: string }>()
  const { data: category, isLoading, isError } = useHelpCenterCategoryBySlug(categorySlug)
  const productTitle = intl.formatMessage(faqsLabelMessage)

  usePageViewTracking({
    enabled: !!category,
    metadata: category ? { categorySlug: category.slug, categoryTitle: category.title } : undefined
  })

  if (isLoading) {
    return <FullScreenLoading />
  }

  if (isError) {
    return (
      <PageLayout>
        <ErrorPage />
      </PageLayout>
    )
  }

  if (!category) {
    return <Redirect to={Paths.FAQS} />
  }

  const navState: HelpCenterArticleNavState = {
    from: { categorySlug: category.slug, categoryTitle: category.title }
  }

  return (
    <>
      <Title title={category.title} productTitle={productTitle} />
      <PageLayout
        header={
          <PageBreadcrumbHeader
            breadcrumbs={
              <FaqsBreadcrumbs category={{ title: category.title, slug: category.slug, currentPage: true }} />
            }
          />
        }
      >
        <Typography variant="h2">{category.title}</Typography>
        <SubCategoryNavigation category={category} />
        <CategorySections category={category} articleNavState={navState} />
      </PageLayout>
    </>
  )
}

function SubCategoryNavigation({ category }: { category: HelpCenterCategory }): JSX.Element {
  const categoryPath = helpCenterCategoryPath(category.slug)
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      sx={(theme) => ({
        gap: theme.spacing(theme.tokens.spacing.lg),
        paddingBlockStart: theme.spacing(theme.tokens.spacing.md),
        paddingBlockEnd: theme.spacing(theme.tokens.spacing.xxxl)
      })}
    >
      {category.subCategories.map((subCategory) => (
        <Link
          key={subCategory.id}
          component={RouterLink}
          to={{ pathname: categoryPath, hash: subCategory.slug }}
          variant="body2"
          color="inherit"
        >
          {subCategory.title}
        </Link>
      ))}
    </Stack>
  )
}

function CategorySections({
  category,
  articleNavState
}: {
  category: HelpCenterCategory
  articleNavState: HelpCenterArticleNavState
}): JSX.Element {
  const theme = useTheme()
  return (
    <Stack
      divider={<Divider />}
      spacing={theme.tokens.spacing.xl}
      sx={(t) => ({ marginBlockStart: t.spacing(t.tokens.spacing.xl) })}
    >
      {category.subCategories.map((subCategory) => (
        <SubCategorySection key={subCategory.id} subCategory={subCategory} articleNavState={articleNavState} />
      ))}
    </Stack>
  )
}

function SubCategorySection({
  subCategory,
  articleNavState
}: {
  subCategory: HelpCenterSubCategory
  articleNavState: HelpCenterArticleNavState
}): JSX.Element {
  const theme = useTheme()
  const location = useLocation()

  React.useEffect(() => {
    if (subCategory.slug && location.hash.replace("#", "") === subCategory.slug) {
      document.getElementById(subCategory.slug)?.scrollIntoView()
    }
  }, [location.hash, subCategory.slug])

  return (
    <Box id={subCategory.slug || undefined} sx={(t) => ({ scrollMarginBlockStart: t.spacing(t.tokens.spacing.lg) })}>
      <Typography variant="h5" sx={(t) => ({ marginBlockEnd: t.spacing(t.tokens.spacing.md) })}>
        {subCategory.title}
      </Typography>
      <Stack
        direction="column"
        spacing={theme.tokens.spacing.xs}
        component="ul"
        sx={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        {subCategory.articles.map((article) => (
          <Box key={article.id} component="li">
            <Link
              component={RouterLink}
              to={{ pathname: helpCenterArticlePath(article), state: articleNavState }}
              variant="body1"
              underline="none"
              endIcon={<ForwardIcon fontSize="small" color="primary" sx={{ verticalAlign: "middle" }} />}
            >
              {article.title}
            </Link>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}
